// ============================================================
// OTel Exporter — Invia metriche, span / traces e log al proxy
// C# in formato OTLP JSON (HTTP JSON, non protobuf).
// 
// Endpoint per ambiente:
//   dev/collaudo: https://compass-col.int.compassonline.it/otlp-collector/otlp
//   produzione:   /otlp-collector/otlp (stesso host)
// ============================================================

import { readTrackingParam } from '../../services/trackingParams';

const OTLP_BASE = (() => {
  const hostname = window.location.hostname;
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^172\./.test(hostname);
  if (isDev) {
    // In locale chiama direttamente il proxy di collaudo via HTTPS
    return 'https://compass-col.int.compassonline.it/otlp-collector/otlp';
  }
  // Produzione: stesso host (same-origin, niente CORS)
  return '/otlp-collector/otlp';
})();

function getQuartoDOra() {
  const now = new Date();
  const h = now.getHours().toString().padStart(2, '0');
  const m = Math.floor(now.getMinutes() / 15) * 15;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

function getMediacode() {
  return readTrackingParam('mediacode');
}

// Stack trace troppo lunghi gonfiano inutilmente il payload OTLP
function truncateStack(stack) {
  if (!stack) return undefined;
  return String(stack).slice(0, 2000);
}

// Punto esatto dell'eccezione (file, riga, colonna) fornito da window.onerror.
// È l'unica informazione che identifica gli script iniettati a runtime (tag
// manager, vendor): nei loro stack compare l'URL del documento, non del file,
// quindi senza questi campi non si risale al codice che ha lanciato l'errore.
function buildErrorLocation(details) {
  const d = details || {};
  const location = {};
  if (d.filename) location.filename = String(d.filename);
  if (d.lineno !== undefined && d.lineno !== null) location.lineno = d.lineno;
  if (d.colno !== undefined && d.colno !== null) location.colno = d.colno;
  if (d.errorName) location.error_name = String(d.errorName);
  return location;
}

// ── OTLP JSON helpers ──

function otlpTimestamp() {
  return Date.now() * 1000000; // nanosecondi
}

// ── Generazione ID OTel ──

function generateId(bytes) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('');
}

function generateTraceId() {
  return generateId(16); // 16 bytes = 128 bit
}

function generateSpanId() {
  return generateId(8);  // 8 byte = 64 bit
}

function buildScopeMetrics(scopeName, metrics) {
  return {
    scope: { name: scopeName },
    metrics,
  };
}

function buildCounter(name, value, attributes = {}) {
  return {
    name,
    description: '',
    unit: '1',
    sum: {
      dataPoints: [{
        attributes: Object.entries(attributes).map(([k, v]) => ({
          key: k,
          value: { stringValue: String(v) },
        })),
        startTimeUnixNano: otlpTimestamp(),
        timeUnixNano: otlpTimestamp(),
        asDouble: value,
      }],
      aggregationTemporality: 2, // CUMULATIVE — Grafana Cloud/Mimir rifiuta DELTA in ingest (400) senza un collector che faccia delta->cumulative
      isMonotonic: true,
    },
  };
}

function buildHistogram(name, values, attributes = {}) {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const avg = n ? sorted.reduce((s, v) => s + v, 0) / n : 0;
  // bucket: 0, 1000, 3000, 5000, 10000, 30000, +Inf
  const explicitBounds = [1000, 3000, 5000, 10000, 30000];
  const bucketCounts = explicitBounds.map(b => sorted.filter(v => v <= b).length);
  bucketCounts.push(n - bucketCounts[bucketCounts.length - 1]);

  return {
    name,
    description: '',
    unit: 'ms',
    histogram: {
      dataPoints: [{
        attributes: Object.entries(attributes).map(([k, v]) => ({
          key: k,
          value: { stringValue: String(v) },
        })),
        startTimeUnixNano: otlpTimestamp(),
        timeUnixNano: otlpTimestamp(),
        count: n,
        sum: avg * n,
        bucketCounts,
        explicitBounds,
      }],
      aggregationTemporality: 2, // CUMULATIVE — vedi nota sopra in buildCounter
    },
  };
}

// ── Log OTLP helpers ──

function buildLogRecord(severityText, body, attributes = {}) {
  const severityMap = { INFO: 9, WARN: 13, ERROR: 17 };
  return {
    timeUnixNano: otlpTimestamp(),
    severityNumber: severityMap[severityText] || 9,
    severityText,
    body: { stringValue: body },
    attributes: Object.entries(attributes).map(([k, v]) => ({
      key: k,
      value: { stringValue: String(v) },
    })),
  };
}

// ── Context Holder (JavaScript puro, non React) ──
// Tiene traccia dello span attivo per linkare gli span figli.
// Il sessionId identifica la sessione utente univoca.

const context = {
  traceId: null,
  sessionId: null,
  spanStack: [], // stack degli span attivi (ultimo = parent corrente)
  path: [], // pagine visitate nella sessione, in ordine
};

// Il session.id deve sopravvivere ai reload della pagina: senza persistenza
// ogni caricamento apre una sessione nuova, quindi i conteggi per pagina
// diventano sessioni diverse ad ogni step e il funnel non e' piu' una coorte
// (gli step successivi possono risultare piu' numerosi dei precedenti).
// sessionStorage: vale per la singola scheda, si azzera alla chiusura.
const SESSION_STORAGE_KEY = 'fa3.otel.session';

function canUseSessionStorage() {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage;
  } catch (e) {
    return false; // storage negato (Safari privato, cookie bloccati)
  }
}

function loadStoredSession() {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.sessionId !== 'string' || !parsed.sessionId) return null;
    return {
      sessionId: parsed.sessionId,
      path: Array.isArray(parsed.path) ? parsed.path.filter(p => typeof p === 'string') : [],
    };
  } catch (e) {
    return null;
  }
}

function persistSession() {
  if (!canUseSessionStorage() || !context.sessionId) return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({
      sessionId: context.sessionId,
      path: context.path,
    }));
  } catch (e) {
    // quota o storage negato: si prosegue senza persistenza, non e' bloccante
  }
}

function ensureSessionContext() {
  if (!context.sessionId) initSessionContext();
  return context.sessionId;
}

function initSessionContext() {
  const now = Date.now();
  const stored = loadStoredSession();
  context.traceId = generateTraceId();
  context.sessionId = stored ? stored.sessionId : generateId(8);
  context.spanStack = [];
  context.path = stored ? stored.path.slice() : [];
  persistSession();
  return { traceId: context.traceId, sessionId: context.sessionId };
}

function pushSpanContext(spanId) {
  context.spanStack.push(spanId);
}

function popSpanContext() {
  return context.spanStack.pop();
}

function getParentSpanId() {
  return context.spanStack.length > 0
    ? context.spanStack[context.spanStack.length - 1]
    : undefined;
}

function hasActiveContext() {
  return !!context.traceId;
}

function getSessionId() {
  return context.sessionId;
}

// ── Costruttori span OTLP ──

function buildSpanData(spanName, startTimeNano, endTimeNano, attributes = {}, status = {}, explicitSpanId) {
  const spanId = explicitSpanId || generateSpanId();
  const attrs = Object.entries(attributes).map(([k, v]) => ({
    key: k,
    value: { stringValue: String(v) },
  }));

  return {
    spanId,
    traceId: context.traceId,
    parentSpanId: getParentSpanId(),
    name: spanName,
    kind: 1, // INTERNAL
    startTimeUnixNano: startTimeNano,
    endTimeUnixNano: endTimeNano,
    attributes: attrs,
    status: status.code ? { code: status.code, message: status.message || '' } : { code: 0 },
  };
}

function pushSpan(spanName, startTimeNano, endTimeNano, attributes = {}, status = {}) {
  if (!hasActiveContext()) return null;

  const span = buildSpanData(spanName, startTimeNano, endTimeNano, attributes, status);

  // Invia in modo fire-and-forget, non blocca
  pushTraces([span]).catch(() => {});

  return span;
}

async function pushTraces(spans) {
  if (!spans || spans.length === 0) return false;

  const payload = {
    resourceSpans: [{
      resource: {
        attributes: [
          { key: 'service.name', value: { stringValue: 'fa3.2' } },
          { key: 'service.version', value: { stringValue: '3.2' } },
          { key: 'session.id', value: { stringValue: context.sessionId || '' } },
        ],
      },
      scopeSpans: [{
        scope: { name: 'fa3.2.journey' },
        spans,
      }],
    }],
  };

  try {
    const res = await fetch(`${OTLP_BASE}/v1/traces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) console.warn('[otel] traces push failed:', res.status);
    return res.ok;
  } catch (e) {
    console.debug('[otel] traces push error:', e.message);
    return false;
  }
}

// ── Export functions ──

async function pushMetrics(scopeMetrics) {
  const payload = {
    resourceMetrics: [{
      resource: {
        attributes: [
          { key: 'service.name', value: { stringValue: 'fa3.2' } },
          { key: 'service.version', value: { stringValue: '3.2' } },
        ],
      },
      scopeMetrics: Array.isArray(scopeMetrics) ? scopeMetrics : [scopeMetrics],
    }],
  };

  try {
    const res = await fetch(`${OTLP_BASE}/v1/metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) console.warn('[otel] metrics push failed:', res.status);
    return res.ok;
  } catch (e) {
    // silenzioso, non deve rompere l'app
    console.debug('[otel] metrics push error:', e.message);
    return false;
  }
}

async function pushLogs(logRecords) {
  const payload = {
    resourceLogs: [{
      resource: {
        attributes: [
          { key: 'service.name', value: { stringValue: 'fa3.2' } },
        ],
      },
      scopeLogs: [{
        scope: { name: 'fa3.2.monitor' },
        logRecords: Array.isArray(logRecords) ? logRecords : [logRecords],
      }],
    }],
  };

  try {
    const res = await fetch(`${OTLP_BASE}/v1/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) console.warn('[otel] logs push failed:', res.status);
    return res.ok;
  } catch (e) {
    console.debug('[otel] logs push error:', e.message);
    return false;
  }
}

// ── Metriche specifiche del progetto ──

let landingSent = false;

async function trackLanding() {
  if (landingSent) return;
  landingSent = true;

  const mediacode = getMediacode();
  await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
    buildCounter('fa.landing.users', 1, {
      'mediacode': mediacode || 'vuoto',
      'quarto_dora': getQuartoDOra(),
    }),
  ]));

  await pushLogs(
    buildLogRecord('INFO', 'user_landed', {
      'mediacode': mediacode || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'event': 'landing',
    })
  );
}

async function trackComponentView(pageName) {
  const mediacode = getMediacode();
  const quartoDora = getQuartoDOra();

  await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
    buildCounter('fa.component.viewed', 1, { page: pageName, quarto_dora: quartoDora, mediacode }),
  ]));
}

// Tempo di permanenza dell'utente sul componente (mount -> unmount/navigazione),
// non tempo di caricamento: va inviato alla chiusura della pagina, non al mount.
async function trackComponentDuration(pageName, durationMs) {
  await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
    buildHistogram('fa.component.duration_ms', [durationMs], { page: pageName }),
  ]));
}

// Percorso di navigazione: un log per cambio pagina con la pagina corrente
// e il path cumulato fino a quel punto (session.id, ...). Non sostituisce
// la view/lo span di pagina, si affianca: chiusura sessione e creazione
// appuntamento sono anchor troppo rari/inaffidabili per ricostruire il
// journey, quindi il path va aggiornato ad ogni cambio pagina, che invece
// scatta sempre. L'ultimo log ricevuto per una session.id è il path finale
// (o il punto di abbandono, se l'utente droppa).
async function trackPagePath(pageName) {
  // La home puo' loggare prima che lo span di sessione sia partito: senza
  // questa garanzia il log parte con session.id vuoto e la pagina non viene
  // conteggiata in nessuna sessione.
  ensureSessionContext();
  context.path.push(pageName);
  const path = context.path.join(',');
  persistSession();

  await pushLogs(
    buildLogRecord('INFO', 'page_path', {
      'session.id': context.sessionId || '',
      'mediacode': getMediacode() || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'event': 'page_path',
      'page': pageName,
      'path': path,
    })
  );
}

async function trackComponentError(pageName, errorMessage, errorStack) {
  const quartoDora = getQuartoDOra();

  await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
    buildCounter('fa.component.error', 1, { page: pageName, quarto_dora: quartoDora }),
  ]));

  const stack = truncateStack(errorStack);
  await pushLogs(
    buildLogRecord('ERROR', `component_error: ${errorMessage}`, {
      page: pageName,
      quarto_dora: quartoDora,
      event: 'component_error',
      ...(stack ? { stack } : {}),
    })
  );
}

async function trackCreateAppointment() {
  const mediacode = getMediacode();
  const quartoDora = getQuartoDOra();

  await pushLogs(
    buildLogRecord('INFO', 'create_appointment_called', {
      'mediacode': mediacode || 'vuoto',
      'quarto_dora': quartoDora,
      'event': 'create_appointment',
      // Senza session.id l'evento non e' attribuibile a una sessione e non
      // si puo' calcolare la conversione Riepilogo -> appuntamento.
      'session.id': ensureSessionContext(),
      'path': context.path.join(','),
    })
  );
}

// ── Tracking chiamate ai servizi (durata media) ──

async function trackServiceCall(serviceName, promise) {
  const start = Date.now();
  const quartoDora = getQuartoDOra();

  try {
    const result = await promise;
    const durationMs = Date.now() - start;

    await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
      buildCounter('fa.service.called', 1, { service: serviceName, quarto_dora: quartoDora }),
      buildHistogram('fa.service.duration_ms', [durationMs], { service: serviceName }),
    ]));

    return result;
  } catch (error) {
    const durationMs = Date.now() - start;
    const errorMessage = (error && error.message) || String(error);
    const stack = truncateStack(error && error.stack);

    await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
      buildCounter('fa.service.error', 1, { service: serviceName, quarto_dora: quartoDora }),
      buildHistogram('fa.service.duration_ms', [durationMs], { service: serviceName }),
    ]));

    await pushLogs(
      buildLogRecord('ERROR', `service_error: ${errorMessage}`, {
        service: serviceName,
        quarto_dora: quartoDora,
        event: 'service_error',
        ...(stack ? { stack } : {}),
      })
    );

    throw error;
  }
}

// ── Tracking errori JS non gestiti (window.onerror / unhandledrejection) ──

async function trackGlobalError(errorMessage, source = 'window', errorStack, errorDetails) {
  const quartoDora = getQuartoDOra();
  const stack = truncateStack(errorStack);
  const location = buildErrorLocation(errorDetails);

  await pushMetrics(buildScopeMetrics('fa3.2.monitor', [
    buildCounter('fa.global.error', 1, { source, quarto_dora: quartoDora }),
  ]));

  await pushLogs(
    buildLogRecord('ERROR', `global_error: ${errorMessage}`, {
      source,
      quarto_dora: quartoDora,
      event: 'global_error',
      ...(stack ? { stack } : {}),
      ...location,
    })
  );

  if (hasActiveContext()) {
    const now = Date.now() * 1000000;
    const span = buildSpanData(
      'error:global',
      now,
      now,
      {
        source,
        error: errorMessage,
        'session.id': context.sessionId,
        ...(stack ? { stack } : {}),
        ...location,
      },
      { code: 2, message: errorMessage }
    );
    pushTraces([span]).catch(() => {});
  }
}

// ── Funzioni span per journey utente ──

let sessionSpanId = null;
let sessionEnded = false;

function trackSpanSessionStart() {
  const { traceId, sessionId } = initSessionContext();
  const now = Date.now() * 1000000;

  sessionEnded = false;

  const span = buildSpanData(
    'session',
    now,
    now,
    {
      'session.id': sessionId,
      'mediacode': getMediacode() || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'component': 'app',
    },
    { code: 1 } // UNSET → aperto, verrà chiuso
  );
  sessionSpanId = span.spanId;

  // Registra come span corrente
  pushSpanContext(span.spanId);

  // Invia subito lo span aperto (endTime = startTime, verrà aggiornato alla chiusura)
  pushTraces([span]).catch(() => {});

  return { traceId, sessionId };
}

function trackSpanSessionEnd() {
  if (!sessionSpanId || !hasActiveContext() || sessionEnded) return;
  sessionEnded = true;

  const now = Date.now() * 1000000;

  // Invia uno span aggiornato (misma traceId/spanId, endTime cambiato)
  const span = buildSpanData(
    'session',
    now, // startTime approssimato — Grafana lo userà per durata
    now,
    {
      'session.id': context.sessionId,
      'mediacode': getMediacode() || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'component': 'app',
      'completed': 'true',
    },
    { code: 1 } // OK
  );

  pushTraces([span]).catch(() => {});
  sessionSpanId = null;
  context.spanStack = [];
}

function trackSpanPageStart(pageName) {
  if (!hasActiveContext()) return null;

  const spanId = generateSpanId();
  pushSpanContext(spanId);

  return spanId;
}

function trackSpanPageEnd(pageName, startTimeNano, attributes = {}) {
  if (!hasActiveContext()) return;

  const spanId = popSpanContext();
  const now = Date.now() * 1000000;

  // Non popriamo lo span radice
  const parentId = getParentSpanId();
  const isRoot = !parentId;

  if (isRoot) {
    // È lo span radice, non chiuderlo qui
    pushSpanContext(spanId);
    return;
  }

  const span = buildSpanData(
    `page:${pageName}`,
    startTimeNano,
    now,
    {
      'page': pageName,
      'mediacode': getMediacode() || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'session.id': context.sessionId,
      ...attributes,
    },
    { code: 1 },
    spanId // riusa lo spanId aperto da trackSpanPageStart, così eventuali span
           // figli creati mentre la pagina era montata puntano al parent giusto
  );

  // Se è figlio diretto dello span radice, lo inviamo con parent corretto
  // buildSpanData usa getParentSpanId che ora è lo span radice dopo il pop

  pushTraces([span]).catch(() => {});
}

function trackSpanPageError(pageName, errorMessage, errorStack) {
  if (!hasActiveContext()) return;

  const now = Date.now() * 1000000;
  const stack = truncateStack(errorStack);

  // Errore asincrono durante la pagina — non chiude lo span ma logga
  const span = buildSpanData(
    `error:${pageName}`,
    now,
    now,
    {
      'page': pageName,
      'error': errorMessage,
      'session.id': context.sessionId,
      ...(stack ? { stack } : {}),
    },
    { code: 2, message: errorMessage } // ERROR
  );

  pushTraces([span]).catch(() => {});
}

function trackSpanCreateAppointment() {
  if (!hasActiveContext()) return;

  const now = Date.now() * 1000000;

  const span = buildSpanData(
    'create:appointment',
    now,
    now,
    {
      'mediacode': getMediacode() || 'vuoto',
      'quarto_dora': getQuartoDOra(),
      'session.id': context.sessionId,
      'event': 'create_appointment',
    },
    { code: 1 } // OK
  );

  pushTraces([span]).catch(() => {});
}

export {
  trackLanding,
  trackComponentView,
  trackComponentDuration,
  trackComponentError,
  trackPagePath,
  trackCreateAppointment,
  pushMetrics,
  pushLogs,
  OTLP_BASE,
  // Nuove esportazioni per journey con span
  trackSpanSessionStart,
  trackSpanSessionEnd,
  trackSpanPageStart,
  trackSpanPageEnd,
  trackSpanPageError,
  trackSpanCreateAppointment,
  getSessionId,
  hasActiveContext,
  // Durata media chiamate ai servizi
  trackServiceCall,
  // Errori JS non gestiti
  trackGlobalError,
};
